import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuEllipsis } from 'react-icons/lu';

/**
 * Overflow action menu — viewport-aware, keyboard accessible, theme-aware.
 *
 * items: [{
 *   id, label, onClick,
 *   icon?, description?, danger?, disabled?, dividerBefore?
 * }]
 */
export function ActionMenu({
  items = [],
  label = 'More actions',
  icon,
  align = 'right',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const menuId = useId();
  const itemRefs = useRef([]);

  const updatePosition = useCallback(() => {
    const el = btnRef.current;
    const menu = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const menuWidth = menu?.offsetWidth || 220;
    const menuHeight = menu?.offsetHeight || Math.min(320, (items.length || 1) * 44 + 16);
    const pad = 8;

    let left = align === 'left' ? rect.left : rect.right - menuWidth;
    left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));

    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    let top;
    if (spaceBelow >= menuHeight || spaceBelow >= spaceAbove) {
      top = rect.bottom + 6;
      if (top + menuHeight > window.innerHeight - pad) {
        top = Math.max(pad, window.innerHeight - menuHeight - pad);
      }
    } else {
      top = Math.max(pad, rect.top - menuHeight - 6);
    }

    setCoords({ top, left });
  }, [align, items.length]);

  useEffect(() => {
    if (!open) return undefined;
    updatePosition();
    const raf = requestAnimationFrame(() => {
      updatePosition();
      itemRefs.current.find((n) => n && !n.disabled)?.focus({ preventScroll: true });
    });

    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
        return;
      }
      if (!menuRef.current) return;
      const enabled = itemRefs.current.filter((n) => n && !n.disabled);
      if (!enabled.length) return;
      const currentIdx = enabled.findIndex((n) => n === document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = currentIdx < 0 ? 0 : (currentIdx + 1) % enabled.length;
        enabled[next]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = currentIdx <= 0 ? enabled.length - 1 : currentIdx - 1;
        enabled[next]?.focus();
      } else if (e.key === 'Home') {
        e.preventDefault();
        enabled[0]?.focus();
      } else if (e.key === 'End') {
        e.preventDefault();
        enabled[enabled.length - 1]?.focus();
      }
    };
    const onScroll = () => updatePosition();

    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, updatePosition]);

  if (!items.length) return null;

  itemRefs.current = [];

  const regular = items.filter((item) => !item.danger);
  const danger = items.filter((item) => item.danger);
  const sections = [];
  regular.forEach((item) => {
    const last = sections[sections.length - 1];
    const group = item.group || null;
    if (!last || last.group !== group || item.dividerBefore) {
      sections.push({ key: `s-${sections.length}`, group, items: [item] });
    } else {
      last.items.push(item);
    }
  });
  if (danger.length) {
    sections.push({ key: 'danger', group: null, danger: true, items: danger });
  }
  let renderIndex = 0;

  const renderItem = (item, idx) => (
    <button
      key={item.id}
      type="button"
      role="menuitem"
      ref={(n) => {
        itemRefs.current[idx] = n;
      }}
      className={`rt-action-menu__item${item.danger ? ' rt-action-menu__item--danger' : ''}${
        item.description ? ' rt-action-menu__item--rich' : ''
      }`}
      disabled={item.disabled}
      onClick={() => {
        setOpen(false);
        item.onClick?.();
      }}
    >
      {item.icon ? <span className="rt-action-menu__icon">{item.icon}</span> : null}
      <span className="rt-action-menu__text">
        <span className="rt-action-menu__label">{item.label}</span>
        {item.description ? (
          <span className="rt-action-menu__desc">{item.description}</span>
        ) : null}
      </span>
    </button>
  );

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`rt-btn rt-btn--tertiary rt-btn--icon rt-btn--sm ${className}`.trim()}
        aria-label={label}
        title={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {icon || <LuEllipsis size={16} />}
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className="rt-action-menu"
            role="menu"
            data-rt-modal-keep-escape=""
            style={{ top: coords.top, left: coords.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {sections.map((section, sIdx) => (
              <div
                key={section.key}
                className={`rt-action-menu__section${section.danger ? ' rt-action-menu__section--danger' : ''}`}
                role="group"
                aria-label={section.group || (section.danger ? 'Danger zone' : undefined)}
              >
                {sIdx > 0 && <div className="rt-action-menu__divider" role="separator" />}
                {section.group && <div className="rt-action-menu__group">{section.group}</div>}
                {section.items.map((item) => {
                  const idx = renderIndex;
                  renderIndex += 1;
                  return renderItem(item, idx);
                })}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Compact row action cluster: primary button(s) + overflow menu.
 */
export function RowActions({ primary, menuItems = [], className = '' }) {
  return (
    <div className={`rt-row-actions ${className}`.trim()} onClick={(e) => e.stopPropagation()}>
      {primary}
      {menuItems.length > 0 && <ActionMenu items={menuItems} />}
    </div>
  );
}

export default ActionMenu;
